import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { apiGetClinicalStudiesByStudyAcronymDefinitionOptions } from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { Spinner } from "../../../components/ui-kit/spinner";
import { Tags } from "../../../components/ui-kit/tags";
import { assertStudyAcronym } from "../../../utils/clinical-studies";
import { dateTimeOrNull } from "../../../utils/utils";
import { StudyVisitTable } from "./study-visit-table.tsx";

export function DefinitionRoute(props: { api: ApiConnector }) {
  const { t } = useLingui();
  const { name } = useParams<{ name: string }>();
  const study = assertStudyAcronym(name);

  const { data } = useQuery(
    apiGetClinicalStudiesByStudyAcronymDefinitionOptions({
      path: { study_acronym: study },
    }),
  );

  return !data ? (
    <Spinner />
  ) : (
    <>
      <FormTable>
        <FormRow label={t`Full title`} data={data.full_title} />

        <FormRow label={t`Protocol version`} data={data.protocol_version} />

        <FormRow label={t`Protocol date`} data={data.protocol_date} />

        <FormRow label={t`Study category`} data={data.study_category} />

        <FormRow
          label={t`Study temporal dimension`}
          data={data.study_temporal_dimension}
        />

        <FormRow
          label={t`Study control structure`}
          data={data.study_control_structure}
        />

        <FormRow
          label={t`Study randomization`}
          data={data.study_randomization}
        />

        <FormRow label={t`Study stage`} data={data.study_stage} />

        <FormRow
          label={t`Planned start`}
          data={dateTimeOrNull(data.planned_start, false)}
        />

        <FormRow
          label={t`Planned end`}
          data={dateTimeOrNull(data.planned_end, false)}
        />

        <FormRow
          label={t`Inclusion criteria`}
          data={
            <ul className="list-disc pl-7">
              {data.inclusion_criteria.map(
                (criterion: string, index: number) => (
                  <li key={index}>{criterion}</li>
                ),
              )}
            </ul>
          }
        />

        <FormRow
          label={t`Exclusion criteria`}
          data={
            <ul className="list-disc pl-7">
              {data.exclusion_criteria.map(
                (criterion: string, index: number) => (
                  <li key={index}>{criterion}</li>
                ),
              )}
            </ul>
          }
        />

        <FormRow label={t`Planned patients`} data={data.planned_patients} />

        <FormRow label={t`Comment`} data={data.comment} />

        <FormRow label={t`Tags`} data={<Tags tags={data.tags ?? []} />} />
      </FormTable>

      <h2 className="mt-10 mb-2 text-base font-bold">
        <Trans>Study visits</Trans>
      </h2>

      {data.study_visits.map((visit: any, index: number) => (
        <StudyVisitTable key={index} visit={visit} api={props.api} />
      ))}
    </>
  );
}
